import React from 'react';
import { graphql, PageProps } from 'gatsby';

type ProductItem = {
    id: string;
    title: string;
    slug: string;
    productId?: string;
    summary?: string | null;
    priceFrom?: number | null;
    heroImage?: { file?: { url?: string | null } | null } | null;
    images?: Array<{ file?: { url?: string | null } | null } | null> | null;
};

type QueryData = {
    site?: { siteMetadata?: { siteUrl?: string | null } | null } | null;
    allContentfulTour: { nodes: ProductItem[] };
};

type PageContext = {
    hideHeaderFooter?: boolean;
    exchangeRates?: Record<string, number>;
    defaultPricesISK?: Record<string, number>;
};

const formatPrice = (value: number, currency: string) => {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
            .format(value)
            .replace(/^([^0-9]*)/, '') // drop symbol to match "162.00 EUR"
            .trim() + ` ${currency}`;
    } catch {
        return `${value.toFixed(2)} ${currency}`;
    }
};

const ProductsFeedPage: React.FC<PageProps<QueryData, PageContext>> = ({ data, pageContext, location }) => {
    const siteUrl = data.site?.siteMetadata?.siteUrl || 'https://www.re.is';
    // pick currency from query (?currency=EUR) or provide multi-currency fields
    const params = new URLSearchParams(location.search);
    const requestedCurrency = (params.get('currency') || '').toUpperCase();
    const exchangeRates = pageContext?.exchangeRates || { ISK: 1 };
    const defaultPricesISK = pageContext?.defaultPricesISK || {};
    const buildImageVariants = (rawUrl?: string | null) => {
        if (!rawUrl) return { base: null } as Record<string, string | null>;
        const abs = rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`;
        return {
            base: abs,
            '1_1': `${abs}?w=1440&h=1440&fit=fill&fm=jpg&q=80`,
            '3_2': `${abs}?w=1620&h=1080&fit=fill&fm=jpg&q=80`,
            '4_5': `${abs}?w=1440&h=1800&fit=fill&fm=jpg&q=80`,
        } as Record<string, string | null>;
    };

    const products = (data.allContentfulTour.nodes || []).map((t) => {
        const imageLink = t.heroImage?.file?.url ? `https:${t.heroImage.file.url}` : null;
        const images = (t.images || [])
            .filter((img): img is { file?: { url?: string | null } | null } => !!img)
            .map((img) => buildImageVariants(img?.file?.url ?? null));
        // Prefer backend default price if available; fallback to Contentful
        const backendISK = t.productId ? defaultPricesISK[t.productId] : undefined;
        const priceISK = typeof backendISK === 'number' ? backendISK : (typeof t.priceFrom === 'number' ? t.priceFrom : null);
        // derive other currencies if we have a rate (assumed ISK -> target rate)
        const eur = priceISK && exchangeRates.EUR ? priceISK / exchangeRates.EUR : null;
        const gbp = priceISK && exchangeRates.GBP ? priceISK / exchangeRates.GBP : null;
        const usd = priceISK && exchangeRates.USD ? priceISK / exchangeRates.USD : null;

        const base = {
            title: t.title,
            description: t.summary || '',
            link: `${siteUrl}/${t.slug}`,
            landingPageUrl: siteUrl,
            id: t.id,
            imageLink,
            images,
            availability: 'in_stock',
        };

        // Option A: single currency via ?currency= param
        if (requestedCurrency && priceISK) {
            const map: Record<string, number | null> = {
                ISK: priceISK,
                EUR: eur,
                GBP: gbp,
                USD: usd,
            };
            const v = map[requestedCurrency];
            if (typeof v === 'number') {
                return { ...base, price: formatPrice(v, requestedCurrency) };
            }
        }
        // Option B: multi-currency fields
        return {
            ...base,
            price_ISK: typeof priceISK === 'number' ? formatPrice(priceISK, 'ISK') : null,
            price_EUR: typeof eur === 'number' ? formatPrice(eur, 'EUR') : null,
            price_GBP: typeof gbp === 'number' ? formatPrice(gbp, 'GBP') : null,
            price_USD: typeof usd === 'number' ? formatPrice(usd, 'USD') : null,
        };
    });

    return (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(products)}</pre>
    );
};

export default ProductsFeedPage;

export const query = graphql`
  query ProductsFeedQuery {
    site { siteMetadata { siteUrl } }
    allContentfulTour(filter: { title: { ne: "ekki eyða" } }) {
      nodes {
        id
        title
        slug
        productId
        summary
        priceFrom
        heroImage { file { url } }
        images { file { url } }
      }
    }
  }
`;


